import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import SiteHeader from "../components/SiteHeader";

export default function Districts() {
  const navigate = useNavigate();

  const [states, setStates] = useState([]);
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState(""); // "", "1", "0"
  const [stateFilter, setStateFilter] = useState(""); // "", state_id

  const [form, setForm] = useState({
    id: null,
    state_id: "",
    name: "",
    is_active: 1,
  });

  const isEditing = form.id !== null;

  const pickItems = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.items)) return data.items;
    return [];
  };

  const loadStates = async () => {
    // For admin page, show all states including inactive
    const { data } = await api.get("/api/admin/states");
    return pickItems(data);
  };

  const loadDistricts = async (opts = {}) => {
    const q = opts.search !== undefined ? opts.search : search;
    const active = opts.active !== undefined ? opts.active : activeFilter;
    const state_id = opts.state_id !== undefined ? opts.state_id : stateFilter;

    const { data } = await api.get("/api/admin/districts", {
      params: {
        q: q || undefined,
        active: active === "" ? undefined : active,
        state_id: state_id === "" ? undefined : state_id,
      },
    });

    return pickItems(data);
  };

  const initLoad = async () => {
    try {
      setLoading(true);
      setErr("");

      const st = await loadStates();
      setStates(st);

      // default state filter to first active if none selected
      let defaultStateId = stateFilter;
      if (!defaultStateId && st.length) {
        const firstActive = st.find((x) => x.is_active !== 0) || st[0];
        defaultStateId = String(firstActive.id);
        setStateFilter(defaultStateId);
        setForm((f) => ({ ...f, state_id: defaultStateId }));
      }

      const list = await loadDistricts({ state_id: defaultStateId });
      setItems(list);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load districts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm((f) => ({
      id: null,
      state_id: stateFilter || f.state_id || "",
      name: "",
      is_active: 1,
    }));
  };

  const selectedStateName = useMemo(() => {
    const s = states.find((x) => String(x.id) === String(stateFilter));
    return s?.name || "";
  }, [states, stateFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.state_id) {
      alert("State is required");
      return;
    }
    if (!form.name.trim()) {
      alert("District name is required");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        state_id: parseInt(form.state_id, 10),
        name: form.name.trim(),
        is_active: form.is_active ? 1 : 0,
      };

      if (isEditing) {
        await api.put(`/api/admin/districts/${form.id}`, payload);
      } else {
        await api.post("/api/admin/districts", payload);
      }

      resetForm();
      const list = await loadDistricts();
      setItems(list);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to save district");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setForm({
      id: item.id,
      state_id: String(item.state_id),
      name: item.name || "",
      is_active: item.is_active === 0 ? 0 : 1,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this district?")) return;

    try {
      setLoading(true);
      await api.delete(`/api/admin/districts/${id}`);
      const list = await loadDistricts();
      setItems(list);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to delete district");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const list = await loadDistricts({ search, active: activeFilter, state_id: stateFilter });
      setItems(list);
      setErr("");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load districts");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setSearch("");
    setActiveFilter("");
    // keep the state filter
    await handleSearch();
  };

  const onStateFilterChange = async (val) => {
    setStateFilter(val);
    // also keep form state_id in sync (for new adds)
    setForm((f) => ({ ...f, state_id: val, id: null, name: "" }));
    try {
      setLoading(true);
      const list = await loadDistricts({ state_id: val });
      setItems(list);
      setErr("");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load districts");
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    page: { minHeight: "100vh", background: "#f1f5f9" },
    container: { maxWidth: 980, margin: "0 auto", padding: 16 },
    card: {
      background: "#fff",
      borderRadius: 10,
      padding: 16,
      boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
      marginBottom: 16,
    },
    input: {
      width: "100%",
      padding: "10px 12px",
      borderRadius: 8,
      border: "1px solid #cbd5e1",
      fontSize: 14,
    },
    label: { fontSize: 13, fontWeight: 600, marginBottom: 4, display: "block" },
    btnPrimary: {
      background: "#0f766e",
      color: "#fff",
      border: "none",
      borderRadius: 999,
      padding: "8px 18px",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
    },
    btnGhost: {
      background: "#e2e8f0",
      borderRadius: 999,
      padding: "8px 16px",
      border: "none",
      fontSize: 14,
      cursor: "pointer",
    },
    pill: (active) => ({
      display: "inline-block",
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      background: active ? "#dcfce7" : "#fee2e2",
      color: active ? "#166534" : "#991b1b",
      border: `1px solid ${active ? "#bbf7d0" : "#fecaca"}`,
    }),
  };

  return (
    <div style={styles.page}>
      <SiteHeader showAdmin />

      <div style={styles.container}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
          <h2 style={{ fontSize: 20, margin: 0 }}>District Master</h2>
          <button onClick={() => navigate("/dashboard")} style={styles.btnGhost}>
            ← Back
          </button>
        </div>

        {/* FORM CARD */}
        <div style={styles.card}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={styles.label}>State *</label>
                <select
                  style={styles.input}
                  value={form.state_id}
                  onChange={(e) => setForm((f) => ({ ...f, state_id: e.target.value }))}
                >
                  <option value="">Select state</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} {s.is_active === 0 ? "(Inactive)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={styles.label}>District Name *</label>
                <input
                  style={styles.input}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Enter district name"
                />
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
              <input
                id="district_active"
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked ? 1 : 0 }))}
              />
              <label htmlFor="district_active" style={{ fontSize: 13, color: "#0f172a" }}>
                Active
              </label>
            </div>

            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button style={styles.btnPrimary} type="submit" disabled={loading}>
                {isEditing ? "Update District" : "Add District"}
              </button>

              {isEditing && (
                <button type="button" style={styles.btnGhost} onClick={resetForm} disabled={loading}>
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        {/* SEARCH + LIST */}
        <div style={styles.card}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select
              style={{ ...styles.input, maxWidth: 260 }}
              value={stateFilter}
              onChange={(e) => onStateFilterChange(e.target.value)}
              title="Filter by State"
            >
              <option value="">All States</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.is_active === 0 ? "(Inactive)" : ""}
                </option>
              ))}
            </select>

            <input
              style={{ ...styles.input, maxWidth: 260 }}
              placeholder="Search district..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <select
              style={{ ...styles.input, maxWidth: 170 }}
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>

            <button style={styles.btnGhost} type="button" onClick={handleSearch}>
              Search
            </button>

            <button style={styles.btnGhost} type="button" onClick={handleReset}>
              Reset
            </button>
          </div>

          <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
            Showing districts {stateFilter ? `for State: ${selectedStateName}` : ""}
          </div>

          {loading ? (
            <div style={{ padding: 12 }}>Loading...</div>
          ) : err ? (
            <div style={{ color: "#b91c1c", marginTop: 10 }}>{err}</div>
          ) : !items.length ? (
            <div style={{ padding: 12, color: "#64748b" }}>No districts found</div>
          ) : (
            <div style={{ marginTop: 12, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={{ padding: 10, textAlign: "left" }}>State</th>
                    <th style={{ padding: 10, textAlign: "left" }}>District</th>
                    <th style={{ padding: 10, textAlign: "left" }}>Status</th>
                    <th style={{ padding: 10, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const active = item.is_active !== 0;
                    return (
                      <tr key={item.id} style={{ borderTop: "1px solid #e2e8f0" }}>
                        <td style={{ padding: 10 }}>{item.state_name || "-"}</td>
                        <td style={{ padding: 10 }}>{item.name}</td>
                        <td style={{ padding: 10 }}>
                          <span style={styles.pill(active)}>{active ? "Active" : "Inactive"}</span>
                        </td>
                        <td style={{ padding: 10, textAlign: "right", whiteSpace: "nowrap" }}>
                          <button
                            type="button"
                            onClick={() => handleEdit(item)}
                            style={{ ...styles.btnGhost, padding: "4px 12px", marginRight: 6, fontSize: 13 }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(item.id)}
                            style={{
                              ...styles.btnGhost,
                              padding: "4px 12px",
                              background: "#fee2e2",
                              border: "1px solid #fecaca",
                              fontSize: 13,
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
                Note: District delete will fail if taluks exist under the district.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
